import { ReactNode, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { isAuthRequired } from '@/lib/environment';
import { AuthModal } from '@/components/AuthModal';
import { AppLoader } from '@/components/AppLoader';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// Keys for storing authorized device-team pairing in localStorage (must match QRCodeLogin)
const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';
const DEVICE_AUTHORIZED_DEVICE_KEY = 'team_v_authorized_device_id';
const DEVICE_ID_KEY = 'team_v_device_id';

// UUID v1-5 shape; rejects non-UUID localStorage tampering early.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Extend window type for initial loader
declare global {
  interface Window {
    hideInitialLoader?: () => void;
  }
}

interface AuthGateProps {
  children: ReactNode;
  /** Force require auth even in dev mode */
  forceAuth?: boolean;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

const isAuthDebugEnabled = () => {
  try {
    return sessionStorage.getItem('auth_gate_debug_enabled') === '1';
  } catch {
    return false;
  }
};

// Log for debugging auth flow (opt-in)
const logDebug = (message: string, data?: any) => {
  if (!isAuthDebugEnabled()) return;
  const timestamp = new Date().toISOString();
  const logEntry = `[AuthGate ${timestamp}] ${message}`;
  console.log(logEntry, data || '');

  // Store in sessionStorage for debugging
  try {
    const logs = JSON.parse(sessionStorage.getItem('auth_gate_debug') || '[]');
    logs.push({ timestamp, message, data });
    if (logs.length > 50) logs.shift();
    sessionStorage.setItem('auth_gate_debug', JSON.stringify(logs));
  } catch {
    // ignore storage errors
  }
};

/**
 * SEC-M6: Read the localStorage device-authorization hint. The result is a
 * *candidate* — the caller MUST server-verify via `verifyDeviceAuthorization`
 * before granting access. Returns null if the hint is missing or malformed.
 */
function readDeviceAuthHint(): { teamId: string; deviceId: string } | null {
  try {
    const teamId = localStorage.getItem(DEVICE_AUTHORIZED_TEAM_KEY);
    const pairedDeviceId = localStorage.getItem(DEVICE_AUTHORIZED_DEVICE_KEY);
    const deviceId = pairedDeviceId || localStorage.getItem(DEVICE_ID_KEY);
    if (!teamId || !deviceId) return null;
    if (!UUID_RE.test(teamId) || !UUID_RE.test(deviceId)) return null;
    return { teamId, deviceId };
  } catch {
    return null;
  }
}

/**
 * SEC-M6: Server-verify a (deviceId, teamId) pair via the
 * `is_device_authorized_for_team` RPC. Returns false on any error so a
 * tampered or stale localStorage hint can never grant access.
 */
async function verifyDeviceAuthorization(deviceId: string, teamId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_device_authorized_for_team', {
      _device_id: deviceId,
      _team_id: teamId,
    });
    if (error) {
      console.warn('[AuthGate] device authorization verification failed', error);
      return false;
    }
    return data === true;
  } catch (e) {
    console.warn('[AuthGate] device authorization verification threw', e);
    return false;
  }
}

function clearDeviceAuthHint() {
  try {
    localStorage.removeItem(DEVICE_AUTHORIZED_TEAM_KEY);
    localStorage.removeItem(DEVICE_AUTHORIZED_DEVICE_KEY);
  } catch {
    // ignore
  }
}

/**
 * AuthGate wraps content that requires authentication.
 * 
 * In development: Shows children without requiring login
 * In production: Shows login prompt if not authenticated
 * 
 * Also supports device authorization via QR code for shared devices (e.g., TVs)
 */
export function AuthGate({ children, forceAuth = false, loadingComponent }: AuthGateProps) {
  const { isAuthenticated, loading } = useAuthContext();
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [contentReady, setContentReady] = useState(false);
  const [deviceAuth, setDeviceAuth] = useState<{ authorized: boolean; teamId: string | null }>({ authorized: false, teamId: null });
  const [devBypassLoading, setDevBypassLoading] = useState(false);

  const requireAuth = forceAuth || isAuthRequired();

  // Optimistic auth: if a stored session exists AND it is well-formed and not
  // expired, assume authenticated and show content immediately. Auth will restore
  // in the background — if it fails, the user is redirected to login.
  //
  // SEC-M8: validate the stored session shape + expiry before trusting it. The
  // mere *presence* of an `sb-<project>-auth-token` key was previously enough to
  // briefly render the protected app shell, which an attacker could trigger by
  // writing arbitrary content to localStorage, and which also flashed protected
  // UI for users whose stored session had silently expired.
  const hasStoredSession = useMemo(() => {
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      if (!projectId) return false;
      const storageKey = `sb-${projectId}-auth-token`;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { access_token?: unknown; refresh_token?: unknown; expires_at?: unknown };
      if (typeof parsed?.access_token !== 'string' || parsed.access_token.length < 20) return false;
      if (typeof parsed?.refresh_token !== 'string' || parsed.refresh_token.length < 10) return false;
      // expires_at is unix seconds. Allow a 60s skew but never trust a clearly
      // expired token for optimistic rendering.
      if (typeof parsed?.expires_at === 'number' && parsed.expires_at * 1000 < Date.now() - 60_000) return false;
      return true;
    } catch {
      return false;
    }
  }, []);
  const optimisticAuth = hasStoredSession && loading;

  // SEC-M6: server-verify the localStorage device-authorization hint. Until
  // the RPC confirms the (device_id, team_id) pair is backed by an authorized
  // `device_auth_codes` row, treat the device as unauthorized. A tampered or
  // stale hint is wiped from localStorage so subsequent boots show the
  // welcome screen instead of the protected shell.
  useEffect(() => {
    const hint = readDeviceAuthHint();
    if (!hint) {
      logDebug('No device authorization hint in localStorage');
      setDeviceAuth({ authorized: false, teamId: null });
      return;
    }

    let cancelled = false;
    logDebug('Verifying device authorization with server', hint);
    verifyDeviceAuthorization(hint.deviceId, hint.teamId).then((ok) => {
      if (cancelled) return;
      if (ok) {
        logDebug('Device authorization confirmed by server', hint);
        setDeviceAuth({ authorized: true, teamId: hint.teamId });
      } else {
        logDebug('Device authorization rejected by server — clearing hint', hint);
        clearDeviceAuthHint();
        setDeviceAuth({ authorized: false, teamId: null });
      }
    });

    return () => { cancelled = true; };
  }, []);

  // Hide the initial HTML loader once React takes over
  useEffect(() => {
    if (typeof window !== 'undefined' && window.hideInitialLoader) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        window.hideInitialLoader?.();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Show shell immediately to avoid white flashes.
  useEffect(() => {
    setContentReady(true);
  }, []);

  // Trigger content fade-in after auth resolves
  useEffect(() => {
    if (!loading) {
      logDebug('Auth loading complete', { isAuthenticated, deviceAuth });
      return undefined;
    }
  }, [loading, isAuthenticated, deviceAuth]);

  // Never block the app indefinitely if auth loading gets stuck.
  // Dev: bypass immediately. Production: allow a short grace period, then
  // always unblock UI (session restore can still complete in background).
  useEffect(() => {
    if (!requireAuth) {
      setDevBypassLoading(true);
      return;
    }

    if (loading) {
      // If we have a stored session, bypass immediately (optimistic auth)
      if (hasStoredSession) {
        setDevBypassLoading(true);
        return;
      }

      // No stored session: short grace period (1s) then show login
      const timer = setTimeout(() => {
        console.warn('[AuthGate] No stored session and auth loading timed out, showing login');
        setDevBypassLoading(true);
      }, 1000);

      return () => clearTimeout(timer);
    }

    setDevBypassLoading(false);
  }, [loading, requireAuth]);

  // If we bypass loading, ensure content becomes visible.
  useEffect(() => {
    if (devBypassLoading) {
      setContentReady(true);
    }
  }, [devBypassLoading]);

  // Handle QR authorization callback
  const handleQRAuthorized = (teamId: string) => {
    logDebug('QR authorization callback received', { teamId });
    setDeviceAuth({ authorized: true, teamId });
    setShowAuthModal(false);

    // Force a full page reload so that all contexts and providers re-initialize
    // with the completed device authorization.
    window.location.reload();
  };

  // In dev mode without forceAuth, always show children
  if (!requireAuth) {
    logDebug('Dev mode - showing children without auth');
    return (
      <div className={cn(
        "transition-opacity duration-300",
        contentReady || devBypassLoading ? "opacity-100" : "opacity-0"
      )}>
        {children}
      </div>
    );
  }

  // Check if user is authenticated OR device is authorized
  // Check if user is authenticated OR device is authorized OR optimistically assumed
  const hasAccess = isAuthenticated || deviceAuth.authorized || optimisticAuth;

  logDebug('Access check', { isAuthenticated, deviceAuthorized: deviceAuth.authorized, optimisticAuth, hasAccess });

  // Show loader only if no stored session and auth is still loading
  if (loading && !devBypassLoading && !optimisticAuth) {
    return loadingComponent || <AppLoader isLoading={true} message="Controleren..." minDisplayTime={0} />;
  }

  // In production (or forceAuth), require authentication or device authorization
  if (!hasAccess) {
    return (
      <>
        <WelcomeScreen
          onLogin={() => {
            setAuthMode('login');
            setShowAuthModal(true);
          }}
          onSignup={() => {
            setAuthMode('signup');
            setShowAuthModal(true);
          }}
          teamName={teamSlug}
        />

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode={authMode}
          onQRAuthorized={handleQRAuthorized}
        />
      </>
    );
  }

  return (
    <div className={cn(
      "transition-opacity duration-300",
      "opacity-100"
    )}>
      {children}
    </div>
  );
}
